import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import Lenis from "lenis";
import { settings } from "./portfolio";
import ReactGA from "react-ga";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Experience from "./components/Experience";
import Skills from "./components/Skills";
import Projects from "./components/Projects";
import Education from "./components/Education";
import Blogs from "./components/Blogs";
import Awards from "./components/Awards";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import StarsCanvas from "./components/StarsCanvas";
import CustomCursor from "./components/CustomCursor";
import LoadingScreen from "./components/LoadingScreen";
import SectionDivider from "./components/SectionDivider";
import AmbientGlow from "./components/AmbientGlow";
import CommandPalette from "./components/CommandPalette";
import { initAudio } from "./utils/soundEngine";

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 50,
    restDelta: 0.001,
  });

  return (
    <motion.div
      style={{ scaleX, transformOrigin: "0%" }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-fg/20 z-[51]"
    />
  );
}

function CliModeButton({ onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[48]"
    >
      <div
        className="relative p-[1.5px] rounded-full overflow-hidden shadow-[0_2px_24px_rgba(0,0,0,0.35)] hover:shadow-[0_4px_32px_rgba(0,0,0,0.5)] transition-shadow duration-300"
        style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}
      >
        {/* Moving glowing border beam */}
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, ease: "linear", duration: 3.5 }}
          className="absolute -inset-[200%] pointer-events-none"
          style={{
            willChange: "transform",
            transformOrigin: "center center",
            background:
              "conic-gradient(from 0deg, transparent 0 65%, rgb(var(--color-surface) / 0.85) 85%, transparent 100%)",
          }}
        />
        <motion.button
          onClick={onClick}
          aria-label="Switch to terminal mode (New feature)"
          title="Terminal mode"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
          className="relative flex items-center gap-2 px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-full bg-bg-raised text-fg-muted hover:text-fg transition-colors duration-300 text-[11px] font-mono tracking-wide select-none cursor-pointer touch-manipulation"
        >
          <span
            className="text-[13px] sm:text-[14px] leading-none"
            style={{ animation: "cli-blink 2.4s ease-in-out infinite" }}
          >
            &gt;_
          </span>
          <span className="text-[11px]">terminal</span>
          <span className="text-[9.5px] font-mono font-bold text-emerald-500 uppercase tracking-wider">
            new
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("theme") !== "light";
  });
  const [cliMode, setCliMode] = useState(() => {
    return localStorage.getItem("mode") === "cli";
  });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [wipe, setWipe] = useState(null);
  const [isMobile] = useState(
    () => window.matchMedia("(pointer: coarse)").matches
  );
  const lenisRef = useRef(null);

  useEffect(() => {
    if (settings.googleTrackingID) {
      ReactGA.initialize(settings.googleTrackingID, {
        testMode: process.env.NODE_ENV === "test",
      });
      ReactGA.pageview(window.location.pathname + window.location.search);
    }
  }, []);

  useEffect(() => {
    initAudio();
    const timer = setTimeout(() => setLoading(false), 2100);
    return () => clearTimeout(timer);
  }, []);

  // Lenis smooth scroll — desktop only (native momentum scroll is faster on touch)
  useEffect(() => {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      infinite: false,
    });
    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => lenis.destroy();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    const root = document.documentElement;
    if (cliMode) {
      root.classList.add("cli-mode");
    } else {
      root.classList.remove("cli-mode");
    }
  }, [cliMode]);

  const toggleTheme = useCallback(
    (e) => {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
      if (e && e.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
      const targetDark = !darkMode;

      setWipe({
        x,
        y,
        bg: targetDark ? "rgb(10,10,10)" : "rgb(250,250,250)",
      });

      setTimeout(() => setDarkMode(targetDark), 280);
      setTimeout(() => setWipe(null), 600);
    },
    [darkMode]
  );

  const toggleCliMode = useCallback(
    (e) => {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;
      if (e && e.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height / 2;
      }
      const enteringCli = !cliMode;

      setWipe({
        x,
        y,
        bg: enteringCli
          ? "rgb(12,14,20)"
          : darkMode
          ? "rgb(10,10,10)"
          : "rgb(250,250,250)",
      });

      setTimeout(() => {
        setCliMode(enteringCli);
        localStorage.setItem("mode", enteringCli ? "cli" : "gui");
      }, 280);
      setTimeout(() => setWipe(null), 600);
    },
    [cliMode, darkMode]
  );

  return (
    <>
      <CustomCursor hidden={cliMode} />
      <ScrollProgress />

      <AnimatePresence>
        {loading && <LoadingScreen key="loader" />}
      </AnimatePresence>

      {/* Wipe transition overlay (shared by theme toggle and CLI toggle) */}
      <AnimatePresence>
        {wipe && (
          <motion.div
            key="wipe"
            initial={{ clipPath: `circle(0px at ${wipe.x}px ${wipe.y}px)` }}
            animate={{
              clipPath: `circle(150vmax at ${wipe.x}px ${wipe.y}px)`,
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.52, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[9990] pointer-events-none"
            style={{ backgroundColor: wipe.bg, willChange: "clip-path" }}
          />
        )}
      </AnimatePresence>

      {/* CLI mode: full-screen iframe */}
      <AnimatePresence>
        {cliMode && (
          <motion.div
            key="cli-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[49] bg-[#0c0e14]"
          >
            <iframe
              src="https://cli-durvesh.vercel.app/"
              title="Terminal portfolio"
              className="w-full h-full border-0"
              allow="clipboard-write"
            />
            {/* Back to visual button */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.4,
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[50]"
            >
              <div
                className="relative p-[1.5px] rounded-full overflow-hidden shadow-[0_2px_24px_rgba(0,0,0,0.6)] hover:shadow-[0_4px_32px_rgba(0,0,0,0.8)] transition-shadow duration-300"
                style={{
                  WebkitMaskImage: "-webkit-radial-gradient(white, black)",
                }}
              >
                {/* Moving glowing border beam */}
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{
                    repeat: Infinity,
                    ease: "linear",
                    duration: 3.5,
                  }}
                  className="absolute -inset-[200%] pointer-events-none"
                  style={{
                    willChange: "transform",
                    transformOrigin: "center center",
                    background:
                      "conic-gradient(from 0deg, transparent 0 65%, rgba(255,255,255,0.85) 85%, transparent 100%)",
                  }}
                />
                <motion.button
                  onClick={toggleCliMode}
                  aria-label="Switch to visual mode"
                  title="Visual mode"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative flex items-center gap-2 px-3.5 py-2 sm:px-4 sm:py-2.5 rounded-full bg-[#0f1117] text-white/70 hover:text-white transition-colors duration-300 text-[11px] font-mono tracking-wide select-none cursor-pointer touch-manipulation"
                >
                  <span
                    className="text-[13px] sm:text-[14px] leading-none"
                    style={{ animation: "cli-blink 2.4s ease-in-out infinite" }}
                  >
                    &#x2190;
                  </span>
                  <span className="text-[11px]">visual</span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* GUI mode: main portfolio */}
      {!cliMode && (
        <>
          <motion.div
            initial={{ scale: 1.02, opacity: 0.6 }}
            animate={
              loading ? { scale: 1.02, opacity: 0.6 } : { scale: 1, opacity: 1 }
            }
            transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="relative min-h-screen bg-bg text-fg overflow-x-clip"
          >
            {darkMode && !isMobile && <StarsCanvas darkMode={darkMode} />}
            {darkMode && <AmbientGlow />}

            {/* Light mode noise texture overlay */}
            {!darkMode && (
              <div
                className="fixed inset-0 z-[1] pointer-events-none opacity-[0.35] mix-blend-soft-light"
                style={{
                  backgroundRepeat: "repeat",
                  backgroundSize: "200px 200px",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E")`,
                }}
              />
            )}

            <Navbar darkMode={darkMode} toggleTheme={toggleTheme} />

            <main className="relative z-10">
              <Hero darkMode={darkMode} isLoaded={!loading} />

              <SectionDivider />
              <div id="skills" className="section-padding container-main">
                <Skills />
              </div>

              <SectionDivider />
              <div id="experience" className="section-padding container-main">
                <Experience />
              </div>

              <SectionDivider />
              <div id="projects" className="py-20 md:py-28 relative">
                <Projects />
              </div>

              <SectionDivider />
              <div id="education" className="section-padding container-main">
                <Education />
              </div>

              <SectionDivider />
              <div id="blogs" className="section-padding container-main">
                <Blogs />
              </div>

              <SectionDivider />
              <div id="awards" className="section-padding container-main">
                <Awards />
              </div>

              <SectionDivider />
              <div id="contact" className="section-padding container-main">
                <Contact />
              </div>
            </main>

            <Footer />
          </motion.div>

          {/* Global Command Palette (⌘K) */}
          <CommandPalette
            isOpen={paletteOpen}
            setIsOpen={setPaletteOpen}
            toggleCliMode={toggleCliMode}
            toggleTheme={toggleTheme}
            darkMode={darkMode}
          />

          {/* Floating terminal toggle */}
          {!loading && <CliModeButton onClick={toggleCliMode} />}
        </>
      )}
    </>
  );
}

export default App;
